import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Breakpoint definitions matching Tailwind CSS defaults
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * Hook for responsive design utilities
 */
export function useResponsiveDesign() {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint>('sm');

  /**
   * Update window size and current breakpoint
   */
  const updateSize = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    setWindowSize({ width, height });

    // Determine current breakpoint
    let breakpoint: Breakpoint = 'sm';
    if (width >= BREAKPOINTS['2xl']) {
      breakpoint = '2xl';
    } else if (width >= BREAKPOINTS.xl) {
      breakpoint = 'xl';
    } else if (width >= BREAKPOINTS.lg) {
      breakpoint = 'lg';
    } else if (width >= BREAKPOINTS.md) {
      breakpoint = 'md';
    } else {
      breakpoint = 'sm';
    }

    setCurrentBreakpoint(breakpoint);
  }, []);

  /**
   * Set up resize listener
   */
  useEffect(() => {
    updateSize();

    const handleResize = () => {
      updateSize();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateSize]);

  /**
   * Check if current screen size is at or above a breakpoint
   */
  const isBreakpoint = useCallback(
    (breakpoint: Breakpoint): boolean => {
      return windowSize.width >= BREAKPOINTS[breakpoint];
    },
    [windowSize.width],
  );

  /**
   * Check if current screen size is below a breakpoint
   */
  const isBelowBreakpoint = useCallback(
    (breakpoint: Breakpoint): boolean => {
      return windowSize.width < BREAKPOINTS[breakpoint];
    },
    [windowSize.width],
  );

  /**
   * Check if current screen size is between two breakpoints
   */
  const isBetweenBreakpoints = useCallback(
    (min: Breakpoint, max: Breakpoint): boolean => {
      return windowSize.width >= BREAKPOINTS[min] && windowSize.width < BREAKPOINTS[max];
    },
    [windowSize.width],
  );

  /**
   * Get responsive value based on current breakpoint
   */
  const getResponsiveValue = useCallback(
    <T>(values: Partial<Record<Breakpoint, T>>, defaultValue: T): T => {
      // Check breakpoints in descending order
      const breakpointOrder: Breakpoint[] = ['2xl', 'xl', 'lg', 'md', 'sm'];

      for (const bp of breakpointOrder) {
        if (windowSize.width >= BREAKPOINTS[bp] && values[bp] !== undefined) {
          return values[bp] as T;
        }
      }

      return defaultValue;
    },
    [windowSize.width],
  );

  /**
   * Get responsive class names
   */
  const getResponsiveClasses = useCallback(
    (classes: Partial<Record<Breakpoint, string>>, defaultClass: string = ''): string => {
      return getResponsiveValue(classes, defaultClass);
    },
    [getResponsiveValue],
  );

  return useMemo(
    () => ({
      windowSize,
      currentBreakpoint,
      isBreakpoint,
      isBelowBreakpoint,
      isBetweenBreakpoints,
      getResponsiveValue,
      getResponsiveClasses,
      // Convenience methods for common breakpoints
      isMobile: isBelowBreakpoint('md'),
      isTablet: isBetweenBreakpoints('md', 'lg'),
      isDesktop: isBreakpoint('lg'),
      isSmallScreen: isBelowBreakpoint('lg'),
      isLargeScreen: isBreakpoint('xl'),
    }),
    [
      windowSize,
      currentBreakpoint,
      isBreakpoint,
      isBelowBreakpoint,
      isBetweenBreakpoints,
      getResponsiveValue,
      getResponsiveClasses,
    ],
  );
}

/**
 * Hook for managing responsive grid layouts
 */
export function useResponsiveGrid() {
  const { currentBreakpoint, getResponsiveValue } = useResponsiveDesign();

  /**
   * Get responsive grid columns
   */
  const getGridColumns = useCallback(
    (config: Partial<Record<Breakpoint, number>> = {}): number => {
      const defaultColumns = {
        sm: 1,
        md: 2,
        lg: 3,
        xl: 4,
        '2xl': 4,
      };

      return getResponsiveValue({ ...defaultColumns, ...config }, 1);
    },
    [getResponsiveValue],
  );

  /**
   * Get responsive gap size
   */
  const getGridGap = useCallback(
    (config: Partial<Record<Breakpoint, string>> = {}): string => {
      const defaultGaps = {
        sm: 'gap-4',
        md: 'gap-6',
        lg: 'gap-6',
        xl: 'gap-8',
        '2xl': 'gap-8',
      };

      return getResponsiveValue({ ...defaultGaps, ...config }, 'gap-4');
    },
    [getResponsiveValue],
  );

  /**
   * Get responsive padding
   */
  const getResponsivePadding = useCallback(
    (config: Partial<Record<Breakpoint, string>> = {}): string => {
      const defaultPadding = {
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8',
        xl: 'p-10',
        '2xl': 'p-12',
      };

      return getResponsiveValue({ ...defaultPadding, ...config }, 'p-4');
    },
    [getResponsiveValue],
  );

  return useMemo(
    () => ({
      currentBreakpoint,
      getGridColumns,
      getGridGap,
      getResponsivePadding,
    }),
    [currentBreakpoint, getGridColumns, getGridGap, getResponsivePadding],
  );
}

/**
 * Hook for managing responsive typography
 */
export function useResponsiveTypography() {
  const { getResponsiveValue } = useResponsiveDesign();

  /**
   * Get responsive text size
   */
  const getTextSize = useCallback(
    (
      size: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl',
      config: Partial<Record<Breakpoint, string>> = {},
    ): string => {
      const sizeMap = {
        xs: {
          sm: 'text-xs',
          md: 'text-sm',
          lg: 'text-sm',
          xl: 'text-base',
          '2xl': 'text-base',
        },
        sm: {
          sm: 'text-sm',
          md: 'text-base',
          lg: 'text-base',
          xl: 'text-lg',
          '2xl': 'text-lg',
        },
        base: {
          sm: 'text-base',
          md: 'text-lg',
          lg: 'text-lg',
          xl: 'text-xl',
          '2xl': 'text-xl',
        },
        lg: {
          sm: 'text-lg',
          md: 'text-xl',
          lg: 'text-xl',
          xl: 'text-2xl',
          '2xl': 'text-2xl',
        },
        xl: {
          sm: 'text-xl',
          md: 'text-2xl',
          lg: 'text-2xl',
          xl: 'text-3xl',
          '2xl': 'text-3xl',
        },
        '2xl': {
          sm: 'text-2xl',
          md: 'text-3xl',
          lg: 'text-3xl',
          xl: 'text-4xl',
          '2xl': 'text-4xl',
        },
        '3xl': {
          sm: 'text-3xl',
          md: 'text-4xl',
          lg: 'text-4xl',
          xl: 'text-5xl',
          '2xl': 'text-5xl',
        },
        '4xl': {
          sm: 'text-4xl',
          md: 'text-5xl',
          lg: 'text-5xl',
          xl: 'text-6xl',
          '2xl': 'text-6xl',
        },
      };

      const defaultSizes = sizeMap[size];
      return getResponsiveValue({ ...defaultSizes, ...config }, defaultSizes.sm);
    },
    [getResponsiveValue],
  );

  return {
    getTextSize,
  };
}
